import * as Actions from '../actions/identificationActions';
import Identification from '../../entities/identification';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type IdentificationAction = { 
    type: string; 
    identification: Identification | null; 
    identificationId: string | Identification; 
    partial: boolean; 
    lastFetch: number; 
    identifications: Identification[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Identification> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Identification[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Identification> | null, firmId: string, userId: string, updates?: Identification[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'identification', 'identifications', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Identification>} data
 * @param {Identification[]} [updates]
 * @returns {ArrayMap<Identification>}
 */
function updateStorage(data: ArrayMap<Identification>, updates?: Identification[]): ArrayMap<Identification> {
	if (logging) console.debug('⮂ identificationReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Identification[]} [updates]
 * @returns {ArrayMap<Identification>}
 */
function updateStorageNoReturn(updates?: Identification[]): void {
	if (logging) console.debug('⮂ identificationReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Identification[]} [data]
 * @returns {ArrayMap<Identification>}
 */
function createState(data?: Identification[] | ArrayMap<Identification>): ArrayMap<Identification> {
	if (!data) return ArrayMapRegistry.clone<Identification>('identification', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Identification>('identification', data.map(e => e));
    else return ArrayMapRegistry.clone<Identification>('identification', data);
}

function identifications(state: ArrayMap<Identification>, action: IdentificationAction): ArrayMap<Identification> {
	if (!state) state = createState();

	let st: ArrayMap<Identification>;

	switch (action.type) {
		// action.identification - the new value of identification with id action.id
		// action.identificationId - id of the target, used if new value is null
		case Actions.IDENTIFICATION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ identificationReducer: FETCH_ONE_SUCCESS', action);

			if (action.identification === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.identificationId)) {
				st = state.clone();
				st.set(action.identification);
				// state.clear();

				updateStorageNoReturn([action.identification]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.identifications
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.identifications
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.identifications
			 !partial, !stateEmpty, fetchEmpty	-> D return identifications in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge identifications in state newer than those in action.identifications
		 */
		case Actions.IDENTIFICATION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.identifications.length === 0;
			const fetched = action.identifications.map(identification => new Identification({...identification, persisted: true}));

			if (logging) console.debug(`⮂ identificationReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} identifications with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ identificationReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ identificationReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Identification> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Identification[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(identification => {
					const current = st.get(identification.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && identification.status === 'D') {
							// Remove it from state
							toUpdate.push(identification);
							st.delete(identification);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (identification.addedOn > current.addedOn) {
								toUpdate.push(identification);
								st.set(identification);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (identification.status !== 'D') {
							toUpdate.push(identification);
							st.set(identification);
						}
						else if (logging) console.debug('⮂ identificationReducer: partialMerge: skipping deleted identification not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ identificationReducer: empty: identifications', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ identificationReducer: fullMerge: identifications', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localIdentification => {
                    // If this identification has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localIdentification.persisted) {
                        const fetchedIdentification = st.get(localIdentification.id);
                        if (!fetchedIdentification || localIdentification.addedOn > fetchedIdentification.addedOn) st.set(localIdentification);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.IDENTIFICATION_FETCH_ONE_FAILURE:
			console.error('IDENTIFICATION_FETCH_ONE_FAILURE', action.identificationId, action.err);

			// No update to storage required
			return state;

		case Actions.IDENTIFICATION_FETCH_FAILURE:
			console.error('IDENTIFICATION_FETCH_FAILURE', action.identificationId, action.err);

			// No update to storage required
			return state;

		case Actions.IDENTIFICATION_RESTORE: {
		    let identification = new Identification({...action.identification});
            st = state.clone();

            const existing = st.get(identification.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (identification.addedOn >= existing.addedOn) {
                    st.set(identification);
                    return updateStorage(st, [identification]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (identification.status === 'D') return state;

                st.set(identification);
                return updateStorage(st, [identification]);
            }
        }
            
		case Actions.IDENTIFICATION_SAVE: {
		    // console.debug('⮂ IDENTIFICATION_SAVE reducer', action.identification?.id);
		    
			// clone the identification
			let identification = new Identification({...action.identification});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new identification in the state
			// If this is a create, add to the queue
			const _identification = st.get(identification.id);

			if (!_identification) st.set(identification);
			else {
				if (identification.addedOn >= _identification.addedOn) st.set(identification);
			}

			return updateStorage(st, [identification]);
		}

		case Actions.IDENTIFICATION_SAVE_MANY: {
		    if (logging) console.debug('⮂ IDENTIFICATION_SAVE_MANY reducer', action.identifications.map(identification => identification.id).join(','));
		    
			// clone the identifications
			let toProcess = action.identifications.map(identification => new Identification(identification));

			st = state.clone();
			// state.clear();

			const updates: Identification[] = [];

			toProcess.forEach(identification => {
				// Add or update the new identification in the state
				// If this is a create, add to the queue
				const existing = st.get(identification.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (identification.status !== 'D') {
						st.push(identification);
						updates.push(identification);
					}
					else if (logging) console.debug('⮂ IDENTIFICATION_SAVE_MANY: skipping deleted identification not in state');
				}
				else {
					if (identification.addedOn >= existing.addedOn) {
						st.set(identification);
						updates.push(identification);
					}
					else if (logging) console.debug('⮂ Skipping, existing identification had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.IDENTIFICATION_REMOVE: {
			// clone the identification
			const identification = new Identification({...action.identification});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(identification);

			return updateStorage(st, [identification]);
		}

		case Actions.IDENTIFICATION_RESTORE_FAILURE:
		case Actions.IDENTIFICATION_SAVE_FAILURE: {
		    console.warn('IDENTIFICATION_SAVE_FAILURE reducer', action.identification?.id);
		    if (!action.identification?.id) {
				console.warn('IDENTIFICATION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const identification = ensure(Identification, action.identification);
			if (!identification) {
				console.error('Could not find identification after call to save');
				return state;
			}

			st = state.clone();

			const _identificationRef = state.get(identification.id);

			if (_identificationRef && (_identificationRef.addedOn === identification.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _identification = new Identification({..._identificationRef});
				_identification.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_identification.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('identification', _identification).then(r => {
						if (r.isErr()) console.error(`⮂ identificationReducer: failed to save identification with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ identificationReducer: saved identification with fatalError info');
					}).catch(err => console.error(`⮂ identificationReducer: failed to save identification with fatalError info: ${err.message}`));

				st.set(_identification);
			}

			return updateStorage(st);
		}

		case Actions.IDENTIFICATION_SAVE_MANY_FAILURE: {
		    console.warn('IDENTIFICATION_SAVE_MANY_FAILURE reducer', action.identifications.map(identification => identification.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.IDENTIFICATION_RESTORE_SUCCESS:
		case Actions.IDENTIFICATION_SAVE_SUCCESS: {
			if (!action.identification?.id) {
				console.warn('IDENTIFICATION_SAVE_SUCCESS reducer');
                return state;
			}

			const identificationRef = state.get(action.identification?.id);
			if (!identificationRef) {
				console.warn('Could not find identification after call to save');
                return state;
			}

			st = state.clone();

			if (action.identification.addedOn >= identificationRef.addedOn) {
				st.set(action.identification);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const identification = new Identification({...identificationRef, persisted: true});
				st.set(identification);
			}

			return updateStorage(st, [action.identification]);
		}

		case Actions.IDENTIFICATION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ IDENTIFICATION_SAVE_MANY_SUCCESS reducer', action.identifications.map(identification => identification.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const identifications: Identification[] = [];

			action.identifications.forEach(e => {
				const identificationRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!identificationRef) console.warn('Could not find identification after call to save');
				else if (e.addedOn >= identificationRef.addedOn) {
				    st.set(e);
				    identifications.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const identification = new Identification({...identificationRef, persisted: true});
					st.set(identification);
					identifications.push(identification);
				}
			});

			return updateStorage(st, identifications);
		}

		case Actions.IDENTIFICATION_REMOVE_FAILURE: {
		    console.warn('IDENTIFICATION_REMOVE_FAILURE action', action.identification?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.IDENTIFICATION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.IDENTIFICATION_RESTORE_MANY: {
			if (logging) console.debug('⮂ IDENTIFICATION_RESTORE_MANY reducer', action.identifications.map(identification => identification.id).join(','));

			const toProcess = action.identifications.map(identification => new Identification({...identification}));
			st = state.clone();

			const updates: Identification[] = [];

			toProcess.forEach(identification => {
				const existing = st.get(identification.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (identification.addedOn >= existing.addedOn) {
						st.set(identification);
						updates.push(identification);
					}
					else if (logging) console.debug('⮂ IDENTIFICATION_RESTORE_MANY: skipping older version of identification');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (identification.status !== 'D') {
						st.set(identification);
						updates.push(identification);
					}
					else if (logging) console.debug('⮂ IDENTIFICATION_RESTORE_MANY: skipping deleted identification not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.IDENTIFICATION_REMOVE_MANY: {
			if (logging) console.debug('⮂ IDENTIFICATION_REMOVE_MANY reducer', action.identifications.map(identification => identification.id).join(','));

			const toProcess = action.identifications.map(identification => new Identification({...identification}));
			st = state.clone();

			toProcess.forEach(identification => {
				st.delete(identification);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default identifications;